import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { CircularProgress, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { buildValuesTab } from '../WidgetEditDialog/buildValuesTab';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getValueKey, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, loadValuesIfNeeded, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { useCommonFormUiTranslation } from '../hooks';
import { getItemLayoutSx } from './Utils/getItemLayoutSx';
import { type } from './types';
const WidgetFormRadio = (props) => {
    var _a, _b, _c;
    const data = props.getData();
    const values = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
    const error = getVisibleError(props);
    const readOnly = props.config.readOnly || ((_c = props.rootParams.formParams) === null || _c === void 0 ? void 0 : _c.readOnly);
    const formLabelSuffix = getFormLabelSuffix(props);
    const ariaLabelledBy = `radio-title-${props.id}`;
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, error: !!error, disabled: props.config.disabled, sx: { display: 'flex', alignItems: 'stretch' } },
        props.config.label && (React.createElement(FormLabel, { id: ariaLabelledBy, sx: { mb: 1 }, error: !!error, disabled: props.config.disabled }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        props.internalData.loading && React.createElement(CircularProgress, { color: 'inherit', size: 20 }),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(RadioGroup, { sx: getItemLayoutSx(props.config.style) }, values.map((value, index) => {
                var _a;
                return (React.createElement(FormControlLabel, { key: getValueKey(value.value), disabled: value.disabled, control: React.createElement(Radio, { value: value.value, tabIndex: props.editMode ? -1 : undefined, checked: props.config.valueEquals ? props.config.valueEquals(data, value.value) : data === value.value, onChange: () => {
                            if (readOnly) {
                                return;
                            }
                            props.setData(value.value);
                            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                        }, autoFocus: ((_a = (!props.editMode && props.config.autoFocus)) !== null && _a !== void 0 ? _a : false) && index === 0, inputProps: {
                            readOnly: readOnly,
                            'aria-describedby': ariaDescribedBy,
                        }, disableTouchRipple: readOnly, sx: readOnly ? { cursor: 'default' } : undefined, "aria-labelledby": ariaLabelledBy }), label: value.label, sx: readOnly ? { cursor: 'default' } : undefined }));
            }))),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormRadioEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('common.select') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            readOnly: true,
            tooltip: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.select',
                    key: 'defaultValue',
                    label: t('widget.widgetForm.defaultValue'),
                    values: props.config.values,
                    valueProvider: props.config.valueProvider,
                },
            ],
        }), buildValuesTab(props), buildValidateTab(props, { required: true, uniqueInList: true }), buildHiddenTab(props, { t }), buildStyleTab(props, {
            align: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'style.layout',
                    label: t('widget.widgetLayout.layout'),
                    values: [
                        { value: 'column', label: t('widget.widgetLayout.column') },
                        { value: 'inline', label: t('widget.widgetLayout.inline') },
                        { value: '2-columns-fit', label: t('widget.widgetLayout.twoColumnsFit') },
                        { value: '2-columns-stretch', label: t('widget.widgetLayout.twoColumnsStretch') },
                    ],
                    style: { layout: 'inline' },
                },
                {
                    type: 'form.content',
                    text: t('widget.widgetLayout.alignmentExplainer'),
                    style: {
                        alert: 'warning',
                    },
                    hidden: {
                        custom: (handler) => {
                            var _a, _b;
                            const align = (_a = handler.up().down('style.align')) === null || _a === void 0 ? void 0 : _a.getData();
                            const layout = (_b = handler.up().down('style.layout')) === null || _b === void 0 ? void 0 : _b.getData();
                            return !(layout === '2-columns-stretch' && (align === 'center' || align === 'right'));
                        },
                    },
                },
            ],
            onLoad: (data) => {
                var _a, _b;
                if (((_a = data.style) === null || _a === void 0 ? void 0 : _a.layout) == null && ((_b = data.style) === null || _b === void 0 ? void 0 : _b.inline)) {
                    data.layout = 'inline';
                }
                return data;
            },
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormRadio, Object.assign({}, props))));
};
export const widgetFormRadio = {
    type,
    init: (props) => {
        if (props.getData() == null && props.config.defaultValue != null) {
            props.setData(props.config.defaultValue);
        }
        props.internalData.loadValues = true;
        loadValuesIfNeeded(props, false, false);
    },
    validate: (props) => {
        var _a, _b, _c, _d, _e;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value == null) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if (((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.uniqueInList) || ((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.uniqueInGrid)) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_e = props.config.validate.uniqueInListMessage) !== null && _e !== void 0 ? _e : error;
            }
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        loadValuesIfNeeded(props, false, false);
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b, _c;
        const data = props.getData();
        if (data) {
            const configValues = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), (_c = configValues.find((configValue) => configValue.value === data)) === null || _c === void 0 ? void 0 : _c.label);
        }
        return [];
    },
    component: WidgetFormRadio,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormRadioEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'radio', label: 'Radio', values: [] }),
};
